import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { StackProps } from '@mui/material';
import { ToastProvider, ToastViewport } from '@natter-co/design-system';
import { RefreshDarkThemeProvider } from 'common/hocs';
import { useBreakpoints } from 'common/hooks';
import { useInheritedVisualRefresh } from 'common/theme';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertContext,
  removeAlertsMatchingContext,
  selectLastDialogAlertByContext,
  selectSnackBarAlertsByContext,
} from 'modules/alerts/state';
import { useAppDispatch, useAppSelector } from 'store';

import { AlertsWrapper } from './Alerts.styled';
import { AlertsToastBridge } from './AlertsToastBridge';
import { AlertDialogContainer, SnackbarContainer } from './components';

type Props = Pick<StackProps, 'sx'> & {
  context: AlertContext;
  justifyContent?: 'start' | 'center' | 'end';
  /** Where the toasts sit under the visual refresh. `sx` and `justifyContent` only place the legacy stack. */
  toastClassName?: string;
};

export const AlertsContainer = ({ sx, context, justifyContent = 'center', toastClassName }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');
  const snackbarAlerts = useAppSelector((state) => selectSnackBarAlertsByContext(state, context));
  const lastDialogAlert = useAppSelector((state) => selectLastDialogAlertByContext(state, context));
  const { isMobile } = useBreakpoints();
  const visualRefresh = useInheritedVisualRefresh();

  useEffect(
    () => () => {
      dispatch(removeAlertsMatchingContext(context));
    },
    [dispatch, context]
  );

  return (
    <>
      {lastDialogAlert && <AlertDialogContainer {...lastDialogAlert} />}

      {visualRefresh ? (
        <ToastProvider>
          <AlertsToastBridge context={context} />
          {/* Live surfaces are dark, but these containers sit beside their dark panels. */}
          {context === 'BROADCAST' || context === 'NATTERING' ? (
            <RefreshDarkThemeProvider>
              <ToastViewport className={toastClassName} closeLabel={t('dismissAlert')} />
            </RefreshDarkThemeProvider>
          ) : (
            <ToastViewport
              className={toastClassName}
              closeLabel={t('dismissAlert')}
              mobilePlacement={context === 'APP' ? 'top' : undefined}
            />
          )}
        </ToastProvider>
      ) : (
        <AlertsWrapper data-testid="AlertsContainer-snackbarAlerts" sx={sx}>
          <AnimatePresence initial={false}>
            {snackbarAlerts?.map((alert) => (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: isMobile ? -50 : 50, scale: 0.3 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
                style={{
                  display: 'flex',
                  justifyContent,
                }}
              >
                <SnackbarContainer alertData={alert} />
              </motion.div>
            ))}
          </AnimatePresence>
        </AlertsWrapper>
      )}
    </>
  );
};
